import axios from 'axios';
import { normalizePriorityStack, PriorityDimension } from '../utils/priorityStack';
import { API_CONFIG, buildAuthApiUrl, getAuthHeaders } from '../config/api';
import secureStorage from '../utils/secureStorage';

// User Profile interfaces — GET/PUT /userCenter/profile `details` shape (current API)
export interface UserProfileDetails {
  id: string | number;
  email: string;
  /** Dialing country code (e.g. "1"); may be null if unset */
  areaCode: string | null;
  phone: string;
  nickname: string;
  firstName?: string;
  lastName?: string;
  country: string;
  street: string;
  unit: string | null;
  city: string;
  state: string;
  zipCode: string;
  avatar: string | null;
  userType: string;
  /** May be null for customer-facing profiles */
  role: string | null;
  /** Backend may return localized labels (e.g. "正常") */
  status: string;
  googleActivateStatus: string;
  createdAt: string;
  updatedAt: string;
}

export interface UserProfileResponse {
  success: boolean;
  code: string;
  message: string;
  details?: UserProfileDetails;
  exceptionRequestId?: string | null;
  exceptionTimestamp?: string | null;
}

export interface AvatarUploadResponse {
  success: boolean;
  code: string;
  message: string;
  details?: {
    avatarUrl: string;
    uploadedAt: string;
    fileSize: number;
    dimensions: {
      width: number;
      height: number;
    };
  };
  exceptionRequestId?: string | null;
  exceptionTimestamp?: string | null;
}

// Update profile request — PUT /userCenter/profile/update body (backend UpdateUserProfileDto)
export interface UpdateUserProfileRequest {
  firstName?: string;
  lastName?: string;
  nickname?: string;
  avatar?: string;
  areaCode?: string;
  phone?: string;
  country?: string;
  street?: string;
  unit?: string;
  city?: string;
  state?: string;
  zipCode?: string;
}

// Smart profile interfaces — GET/PUT /user/smart-profiles `details` shape (user-service API).
// Field names follow frontend camelCase; aligns with smart_search_profile_schema_prd.md with
// minor backend naming differences: backend primary_factor/primaryFactor stores the PRD location setup mode.
export interface SmartProfileCommuteDestination {
  address?: string | null;
  buildingName?: string | null;
  institutionName?: string | null;
  lat?: number | null;
  lng?: number | null;
  name?: string | null;
  schoolBuildingId?: number | null;
}

export interface SmartProfileLivingLocation {
  city?: string | null;
  label?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  neighborhoodName?: string | null;
  radiusKm?: number | null;
  source?: string | null;
}

export interface SmartProfilePetPolicy {
  description?: string | null;
  name?: string | null;
  type?: string | null;
}

export interface UserSmartProfileDetails {
  availableBy?: string | null;
  availableFrom?: string | null;
  bathrooms?: number | null;
  bedrooms?: number | null;
  /** Durable bedroom match mode: exact / at_least / at_most / any */
  bedroomsMode?: string | null;
  /** @deprecated Read-only compatibility for profiles created before at_most. */
  bedroomOptions?: number[];
  commuteDestinations?: SmartProfileCommuteDestination[];
  commuteMethods?: string[];
  /** employed / student / self-employed / unemployed */
  employmentStatus?: string | null;
  institutionName?: string | null;
  /** Legacy default search latitude — prefer livingLocations for new PRD flows */
  latitude?: number | null;
  livingLocations?: SmartProfileLivingLocation[];
  /** Legacy default search longitude — prefer livingLocations for new PRD flows */
  longitude?: number | null;
  maxCommuteMinutes?: number | null;
  moveInDate?: string | null;
  /** fixed / flexible / range */
  moveInWindowKind?: string | null;
  mustHaveAmenities?: string[];
  mustHaveInUnit?: string[];
  parking?: boolean | null;
  petPolicy?: SmartProfilePetPolicy[];
  pets?: boolean | null;
  priceMax?: number | null;
  priceMin?: number | null;
  /** PRD location setup mode: commute_first / area_first / explore_first */
  primaryFactor?: string | null;
  propertyTypes?: string[];
  /** Legacy default search radius — prefer livingLocations[].radiusKm for new PRD flows */
  radiusKm?: number | null;
  /** Ordered priorities; scalar styles remain accepted only for legacy reads. */
  recommendationStyle?: PriorityDimension[] | string | null;
  residenceType?: string | null;
  residenceTypeDetail?: string | null;
}

export interface UserSmartProfileResponse {
  success: boolean;
  code?: string;
  message?: string;
  details?: UserSmartProfileDetails | null;
  exceptionRequestId?: string | null;
  exceptionTimestamp?: string | null;
}

export type UpdateUserSmartProfileRequest = Partial<UserSmartProfileDetails> & {
  /** Reset-only backend control: null attributes are persisted as SQL NULL. */
  resetNullAttributes?: boolean;
};

/** Alias used by Smart Search UI — same shape as UserSmartProfileDetails */
export type SmartProfileDetails = UserSmartProfileDetails;

export type SmartProfileBedroomsMode = 'exact' | 'at_least' | 'min' | 'at_most' | 'max' | 'any' | string;

export const EMPTY_SMART_PROFILE: UserSmartProfileDetails = {
  employmentStatus: null,
  institutionName: null,
  latitude: null,
  longitude: null,
  radiusKm: null,
  priceMin: null,
  priceMax: null,
  bedrooms: null,
  bedroomsMode: null,
  bedroomOptions: [],
  bathrooms: null,
  propertyTypes: [],
  parking: null,
  pets: null,
  petPolicy: [],
  moveInDate: null,
  availableFrom: null,
  availableBy: null,
  moveInWindowKind: null,
  commuteDestinations: [],
  commuteMethods: [],
  maxCommuteMinutes: null,
  livingLocations: [],
  primaryFactor: null,
  recommendationStyle: null,
  mustHaveInUnit: [],
  mustHaveAmenities: [],
  residenceType: null,
  residenceTypeDetail: null,
};

const compactArray = <T>(value: T[] | undefined | null): T[] => (
  Array.isArray(value) ? value : []
);

const normalizeBedroomOptions = (values: unknown): number[] => (
  Array.isArray(values)
    ? Array.from(new Set(values.filter(
      value => Number.isInteger(value) && value >= 0 && value <= 99
    ))).sort((a, b) => a - b)
    : []
);

export const compactAllowedBedroomsForDurableProfile = (
  values: unknown
): Pick<UserSmartProfileDetails, 'bedrooms' | 'bedroomsMode'> => {
  const allowed = normalizeBedroomOptions(values);
  if (allowed.length === 1) {
    return { bedrooms: allowed[0], bedroomsMode: 'exact' };
  }
  if (allowed.length > 1 && allowed.every((value, index) => value === index)) {
    return { bedrooms: allowed[allowed.length - 1], bedroomsMode: 'at_most' };
  }
  return { bedrooms: null, bedroomsMode: 'any' };
};

const getSmartProfileHeaders = () => ({
  ...API_CONFIG.HEADERS,
  ...getAuthHeaders(),
});

const sanitizeSmartProfilePayload = (profile: Partial<UserSmartProfileDetails>): UserSmartProfileDetails => {
  const bedroomMode = String(profile.bedroomsMode || '').trim().toLowerCase();
  const bedroomPreference = bedroomMode === 'allowed_set'
    ? compactAllowedBedroomsForDurableProfile(profile.bedroomOptions)
    : {
      bedrooms: profile.bedrooms ?? null,
      bedroomsMode: profile.bedroomsMode ?? null,
    };

  return {
    employmentStatus: profile.employmentStatus ?? null,
    latitude: profile.latitude ?? null,
    longitude: profile.longitude ?? null,
    radiusKm: profile.radiusKm ?? null,
    priceMin: profile.priceMin ?? null,
    priceMax: profile.priceMax ?? null,
    ...bedroomPreference,
    bathrooms: profile.bathrooms ?? null,
    propertyTypes: compactArray(profile.propertyTypes),
    parking: profile.parking ?? null,
    pets: profile.pets ?? null,
    petPolicy: compactArray(profile.petPolicy),
    moveInDate: profile.moveInDate ?? null,
    availableFrom: profile.availableFrom ?? null,
    availableBy: profile.availableBy ?? null,
    moveInWindowKind: profile.moveInWindowKind ?? null,
    commuteDestinations: compactArray(profile.commuteDestinations).map(destination => ({
      name: destination?.name ?? null,
      lat: destination?.lat ?? null,
      lng: destination?.lng ?? null,
      institutionName: destination?.institutionName ?? null,
      buildingName: destination?.buildingName ?? null,
      address: destination?.address ?? null,
      schoolBuildingId: destination?.schoolBuildingId ?? null,
    })),
    commuteMethods: compactArray(profile.commuteMethods),
    maxCommuteMinutes: profile.maxCommuteMinutes ?? null,
    livingLocations: compactArray(profile.livingLocations).map(location => ({
      source: location?.source ?? null,
      label: location?.label ?? null,
      city: location?.city ?? null,
      neighborhoodName: location?.neighborhoodName ?? null,
      latitude: location?.latitude ?? null,
      longitude: location?.longitude ?? null,
      radiusKm: location?.radiusKm ?? null,
    })),
    primaryFactor: profile.primaryFactor ?? null,
    mustHaveInUnit: compactArray(profile.mustHaveInUnit),
    mustHaveAmenities: compactArray(profile.mustHaveAmenities),
    residenceType: profile.residenceType ?? null,
    residenceTypeDetail: profile.residenceTypeDetail ?? null,
    institutionName: profile.institutionName ?? null,
    recommendationStyle: profile.recommendationStyle == null
      ? null
      : normalizePriorityStack(profile.recommendationStyle),
  };
};

// Notification preference service is paused for this release.
// Keep these types commented for quick re-enable in future versions.
/*
export interface EmailPreferencesDetails {
  id: number;
  userId: number;
  marketingEmails: number;
  notificationEmails: number;
  tourReminders: number;
  bookingUpdates: number;
  unsubscribeToken: string;
  unsubscribeTokenExpiresAt: string;
}

export interface EmailPreferencesResponse {
  success: boolean;
  code: string;
  message: string;
  details?: EmailPreferencesDetails;
  exceptionRequestId?: string | null;
  exceptionTimestamp?: string | null;
}

export interface UpdateEmailPreferencesRequest {
  marketingEmails?: number;
  notificationEmails?: number;
  tourReminders?: number;
  bookingUpdates?: number;
}
*/

class ProfileService {
  async getUserProfile(): Promise<UserProfileDetails> {
    try {
      const url = buildAuthApiUrl(API_CONFIG.ENDPOINTS.USER_PROFILE);
      const token = secureStorage.getToken();
      const headers = {
        ...API_CONFIG.HEADERS,
        ...getAuthHeaders(),
      };

      if (!token) {
        throw new Error('No authentication token available. Please log in.');
      }

      const response = await axios.get(url, {
        headers,
        timeout: API_CONFIG.TIMEOUT,
      });

      const responseData: UserProfileResponse = response.data;

      if (responseData.success && responseData.details) {
        return responseData.details;
      }

      throw new Error(responseData.message || 'Failed to fetch user profile');
    } catch (error: any) {
      console.error('❌ [PROFILE] Failed to fetch user profile:', error);
      console.error('❌ [PROFILE] Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        url: error.config?.url,
      });

      if (error.response?.status === 401) {
        throw new Error('Authentication required. Please log in again.');
      }

      if (error.response?.status === 403) {
        throw new Error('Access denied. You do not have permission to view this profile.');
      }

      if (error.request) {
        throw new Error('Network error: Unable to reach the server');
      }

      throw new Error(error.message || 'An unexpected error occurred while fetching profile');
    }
  }

  async updateUserProfile(data: UpdateUserProfileRequest): Promise<UserProfileDetails> {
    try {
      const url = buildAuthApiUrl(API_CONFIG.ENDPOINTS.USER_PROFILE_UPDATE);
      const token = secureStorage.getToken();
      const headers = {
        ...API_CONFIG.HEADERS,
        ...getAuthHeaders(),
      };

      if (!token) {
        throw new Error('No authentication token available. Please log in.');
      }

      const response = await axios.put(url, data, {
        headers,
        timeout: API_CONFIG.TIMEOUT,
      });

      const responseData: UserProfileResponse = response.data;

      if (responseData.success && responseData.details) {
        return responseData.details;
      }

      throw new Error(responseData.message || 'Failed to update user profile');
    } catch (error: any) {
      console.error('❌ [PROFILE UPDATE] Failed to update user profile:', error);
      console.error('❌ [PROFILE UPDATE] Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        url: error.config?.url,
      });

      if (error.response?.status === 401) {
        throw new Error('Authentication required. Please log in again.');
      }

      if (error.response?.status === 403) {
        throw new Error('Access denied. You do not have permission to update this profile.');
      }

      if (error.response?.status === 400) {
        throw new Error(error.response.data?.message || 'Invalid profile data');
      }

      if (error.request) {
        throw new Error('Network error: Unable to reach the server');
      }

      throw new Error(error.message || 'An unexpected error occurred while updating profile');
    }
  }

  async uploadAvatar(file: File): Promise<NonNullable<AvatarUploadResponse['details']>> {
    try {
      const url = buildAuthApiUrl(API_CONFIG.ENDPOINTS.USER_PROFILE_AVATAR);
      const token = secureStorage.getToken();

      if (!token) {
        throw new Error('No authentication token available. Please log in.');
      }

      const formData = new FormData();
      formData.append('file', file);

      // Do not set Content-Type here; the browser must add the multipart boundary.
      const response = await axios.post<AvatarUploadResponse>(url, formData, {
        headers: getAuthHeaders(),
        timeout: API_CONFIG.TIMEOUT,
      });

      const responseData = response.data;
      if (responseData.success && responseData.details) {
        const cachedProfile = secureStorage.getProfileData();
        if (cachedProfile) {
          secureStorage.setProfileData({
            ...cachedProfile,
            avatar: responseData.details.avatarUrl,
          });
        }
        return responseData.details;
      }

      throw new Error(responseData.message || 'Failed to upload profile picture');
    } catch (error: any) {
      console.error('❌ [PROFILE AVATAR] Failed to upload profile picture:', error);
      console.error('❌ [PROFILE AVATAR] Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        url: error.config?.url,
      });

      if (error.response?.status === 401) {
        throw new Error('Authentication required. Please log in again.');
      }

      if (error.response?.status === 403) {
        throw new Error('Access denied. You do not have permission to update this profile.');
      }

      if (error.response?.status === 400) {
        throw new Error(error.response.data?.message || 'Invalid profile picture');
      }

      if (error.request) {
        throw new Error('Network error: Unable to reach the server');
      }

      throw new Error(error.message || 'An unexpected error occurred while uploading profile picture');
    }
  }

  async getSmartProfile(): Promise<UserSmartProfileDetails | null> {
    try {
      const url = buildAuthApiUrl(API_CONFIG.ENDPOINTS.USER_SMART_PROFILE);
      const headers = getSmartProfileHeaders();

      const response = await axios.get(url, {
        headers,
        timeout: API_CONFIG.TIMEOUT,
      });

      const responseData: UserSmartProfileResponse = response.data;

      if (responseData.success) {
        return responseData.details ? {
          ...responseData.details,
          recommendationStyle: normalizePriorityStack(responseData.details.recommendationStyle),
        } : null;
      }

      throw new Error(responseData.message || 'Failed to fetch smart profile');
    } catch (error: any) {
      console.error('❌ [SMART PROFILE] Failed to fetch smart profile:', error);
      console.error('❌ [SMART PROFILE] Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        url: error.config?.url,
      });

      if (error.response?.status === 401) {
        throw new Error('Authentication required. Please log in again.');
      }

      if (error.response?.status === 403) {
        throw new Error('Access denied. You do not have permission to view this smart profile.');
      }

      if (error.response?.status === 404) {
        throw new Error(error.response?.data?.message || 'Smart profile API is not available in this environment yet.');
      }

      if (error.response) {
        throw new Error(error.response?.data?.message || `Smart profile service error (${error.response.status}).`);
      }

      if (error.request) {
        throw new Error('Network error: Unable to reach the server');
      }

      throw new Error(error.message || 'An unexpected error occurred while fetching smart profile');
    }
  }

  async updateSmartProfile(data: UpdateUserSmartProfileRequest): Promise<UserSmartProfileDetails | null> {
    let requestPayload: UpdateUserSmartProfileRequest | null = null;
    try {
      const url = buildAuthApiUrl(API_CONFIG.ENDPOINTS.USER_SMART_PROFILE);
      const headers = getSmartProfileHeaders();

      const existingProfile = await this.getSmartProfile();
      requestPayload = {
        ...sanitizeSmartProfilePayload({
          ...(existingProfile || {}),
          ...data,
        }),
        resetNullAttributes: data.resetNullAttributes === true,
      };

      const response = await axios.put(url, requestPayload, {
        headers,
        timeout: API_CONFIG.TIMEOUT,
      });

      const responseData: UserSmartProfileResponse = response.data;

      if (responseData.success) {
        // The live Java PUT contract returns ResultInfo<Void>. Keep callers in
        // sync without adding a third request by falling back to the complete,
        // sanitized payload that was accepted by the server.
        const profile = responseData.details ?? sanitizeSmartProfilePayload(requestPayload ?? {});
        return { ...profile, recommendationStyle: normalizePriorityStack(profile.recommendationStyle) };
      }

      throw new Error(responseData.message || 'Failed to update smart profile');
    } catch (error: any) {
      console.error('❌ [SMART PROFILE UPDATE] Failed to update smart profile:', error);
      console.error('❌ [SMART PROFILE UPDATE] Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        url: error.config?.url,
      });
      console.error('❌ [SMART PROFILE UPDATE] Request payload:', JSON.stringify(requestPayload, null, 2));

      if (error.response?.status === 401) {
        throw new Error('Authentication required. Please log in again.');
      }

      if (error.response?.status === 403) {
        throw new Error('Access denied. You do not have permission to update this smart profile.');
      }

      if (error.response?.status === 400) {
        throw new Error(error.response.data?.message || 'Invalid smart profile data');
      }

      if (error.response?.status === 404) {
        throw new Error(error.response?.data?.message || 'Smart profile API is not available in this environment yet.');
      }

      if (error.response) {
        throw new Error(error.response?.data?.message || `Smart profile service error (${error.response.status}).`);
      }

      if (error.request) {
        throw new Error('Network error: Unable to reach the server');
      }

      throw new Error(error.message || 'An unexpected error occurred while updating smart profile');
    }
  }

  async resetSmartProfile(): Promise<UserSmartProfileDetails | null> {
    let requestPayload: UpdateUserSmartProfileRequest | null = null;
    try {
      const url = buildAuthApiUrl(API_CONFIG.ENDPOINTS.USER_SMART_PROFILE);
      const headers = getSmartProfileHeaders();

      requestPayload = {
        ...sanitizeSmartProfilePayload(EMPTY_SMART_PROFILE),
        resetNullAttributes: true,
      };
      const response = await axios.put(url, requestPayload, {
        headers,
        timeout: API_CONFIG.TIMEOUT,
      });

      const responseData: UserSmartProfileResponse = response.data;

      if (responseData.success) {
        return responseData.details ? {
          ...responseData.details,
          recommendationStyle: normalizePriorityStack(responseData.details.recommendationStyle),
        } : null;
      }

      throw new Error(responseData.message || 'Failed to reset smart profile');
    } catch (error: any) {
      console.error('❌ [SMART PROFILE RESET] Failed to reset smart profile:', error);
      console.error('❌ [SMART PROFILE RESET] Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        url: error.config?.url,
      });
      console.error('❌ [SMART PROFILE RESET] Request payload:', JSON.stringify(requestPayload, null, 2));

      if (error.response?.status === 401) {
        throw new Error('Authentication required. Please log in again.');
      }

      if (error.response?.status === 403) {
        throw new Error('Access denied. You do not have permission to reset this smart profile.');
      }

      if (error.response?.status === 404) {
        throw new Error(error.response?.data?.message || 'Smart profile API is not available in this environment yet.');
      }

      if (error.response) {
        throw new Error(error.response?.data?.message || `Smart profile service error (${error.response.status}).`);
      }

      if (error.request) {
        throw new Error('Network error: Unable to reach the server');
      }

      throw new Error(error.message || 'An unexpected error occurred while resetting smart profile');
    }
  }

  /* async getEmailPreferences(userId: string | number): Promise<EmailPreferencesDetails> {
    try {
      const normalizedUserId = String(userId ?? '').trim();
      if (!normalizedUserId) {
        throw new Error('userId is required');
      }

      const url = buildAuthApiUrl(`/email-preferences/${encodeURIComponent(normalizedUserId)}`);
      const token = secureStorage.getToken();
      const headers = {
        ...API_CONFIG.HEADERS,
        ...getAuthHeaders(),
      };

      if (!token) {
        throw new Error('No authentication token available. Please log in.');
      }

      const response = await axios.get(url, {
        headers,
        timeout: API_CONFIG.TIMEOUT,
      });

      const responseData: EmailPreferencesResponse = response.data;
      if (responseData.success && responseData.details) {
        return responseData.details;
      }

      throw new Error(responseData.message || 'Failed to fetch email preferences');
    } catch (error: any) {
      if (error.response?.status === 401) {
        throw new Error('Authentication required. Please log in again.');
      }

      if (error.response?.status === 403) {
        throw new Error('Access denied. You do not have permission to view email preferences.');
      }

      if (error.request) {
        throw new Error('Network error: Unable to reach the server');
      }

      throw new Error(error.message || 'An unexpected error occurred while fetching email preferences');
    }
  } */

  /* async updateEmailPreferences(
    userId: string | number,
    data: UpdateEmailPreferencesRequest
  ): Promise<EmailPreferencesDetails> {
    try {
      const normalizedUserId = String(userId ?? '').trim();
      if (!normalizedUserId) {
        throw new Error('userId is required');
      }

      const url = buildAuthApiUrl(`/email-preferences/${encodeURIComponent(normalizedUserId)}`);
      const token = secureStorage.getToken();
      const headers = {
        ...API_CONFIG.HEADERS,
        ...getAuthHeaders(),
      };

      if (!token) {
        throw new Error('No authentication token available. Please log in.');
      }

      const response = await axios.put(url, data, {
        headers,
        timeout: API_CONFIG.TIMEOUT,
      });

      const responseData: EmailPreferencesResponse = response.data;
      if (responseData.success && responseData.details) {
        return responseData.details;
      }

      throw new Error(responseData.message || 'Failed to update email preferences');
    } catch (error: any) {
      if (error.response?.status === 401) {
        throw new Error('Authentication required. Please log in again.');
      }

      if (error.response?.status === 403) {
        throw new Error('Access denied. You do not have permission to update email preferences.');
      }

      if (error.request) {
        throw new Error('Network error: Unable to reach the server');
      }

      throw new Error(error.message || 'An unexpected error occurred while updating email preferences');
    }
  } */
}

const profileService = new ProfileService();
export default profileService;
